import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * Posuvná oblast, která dole ukáže jemné vyblednutí, dokud pod okrajem něco je.
 * Napovídá, že seznam pokračuje. Na konci seznamu vyblednutí zmizí.
 */
export const ScrollFadeArea = ({
  children,
  className,
  fadeClassName,
}: {
  children: ReactNode
  className?: string
  /** Barva vyblednutí podle pozadí, výchozí je from-background */
  fadeClassName?: string
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [more, setMore] = useState(false)

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return
    setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4)
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    Array.from(el.children).forEach((child) => ro.observe(child))
    return () => ro.disconnect()
  }, [children, update])

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div ref={ref} onScroll={update} className={cn('min-h-0 flex-1 overflow-y-auto', className)}>
        {children}
      </div>
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-background via-background/70 to-transparent transition-opacity duration-200',
          more ? 'opacity-100' : 'opacity-0',
          fadeClassName,
        )}
      />
    </div>
  )
}
